import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Send, Upload, Loader2, X, Sparkles, Wand2, Calendar, Trash2 } from "lucide-react";
import { PageContainer, PageHeader } from "@/components/Page";
import api from "@/lib/api";

const PLACEMENT_OPTIONS = [
  { key: "facebook-feed", label: "FB Feed" },
  { key: "facebook-story", label: "FB Story" },
  { key: "instagram-feed", label: "IG Feed" },
  { key: "instagram-story", label: "IG Story" },
];

const readFileAsDataUrl = (f) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(f);
});

const BulkPost = () => {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [items, setItems] = useState([]); // { key, image_url, caption, hashtags, topic, analyzing, error }
  const [placements, setPlacements] = useState(["facebook-feed", "instagram-feed"]);
  const [appendContact, setAppendContact] = useState(true);
  const [scheduleMode, setScheduleMode] = useState("now");
  const [startAt, setStartAt] = useState(() => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setSeconds(0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [interval, setIntervalMin] = useState(60);
  const [analyzingAll, setAnalyzingAll] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get("/brands").then(({ data }) => {
      setBrands(data || []);
      if (data && data.length) setBrandId((prev) => prev || data[0].id);
    }).catch(() => toast.error("Failed to load brands"));
  }, []);

  const onFilesChosen = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    if (items.length + files.length > 30) { toast.error("Max 30 images per batch"); return; }
    const newItems = [];
    for (const f of files) {
      if (f.size > 8 * 1024 * 1024) {
        toast.error(`${f.name} is > 8 MB — skipped`);
        continue;
      }
      const dataUrl = await readFileAsDataUrl(f);
      newItems.push({ key: `${Date.now()}-${Math.random()}`, image_url: dataUrl, name: f.name, caption: "", hashtags: "", topic: "", analyzing: false, analyzed: false });
    }
    setItems((prev) => [...prev, ...newItems]);
  };

  const removeItem = (key) => setItems((prev) => prev.filter((it) => it.key !== key));

  const analyzeAll = async () => {
    if (!brandId) { toast.error("Pick a brand first"); return; }
    const targets = items.map((it, idx) => ({ ...it, idx })).filter((x) => !x.analyzed);
    if (!targets.length) { toast.info("Nothing to analyze"); return; }
    setAnalyzingAll(true);
    setItems((prev) => prev.map((it) => (!it.analyzed ? { ...it, analyzing: true, error: null } : it)));
    try {
      const req = { brand_id: brandId, items: targets.map((t) => ({ index: t.idx, image_url: t.image_url })) };
      const { data } = await api.post("/quick-post/bulk-analyze", req, { timeout: 240000 });
      const byIndex = Object.fromEntries((data.results || []).map((r) => [r.index, r]));
      setItems((prev) => prev.map((it, idx) => {
        const r = byIndex[idx];
        if (!r) return { ...it, analyzing: false };
        if (!r.ok) return { ...it, analyzing: false, error: r.error || "Failed" };
        return {
          ...it, analyzing: false, analyzed: true, error: null,
          caption: r.caption || it.caption,
          hashtags: r.hashtags || it.hashtags,
          topic: r.topic || it.topic,
          subject: r.subject,
        };
      }));
      const ok = (data.results || []).filter((r) => r.ok).length;
      const fail = (data.results || []).length - ok;
      toast.success(`Analyzed ${ok}${fail ? ` · ${fail} failed` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyze failed");
      setItems((prev) => prev.map((it) => ({ ...it, analyzing: false })));
    } finally {
      setAnalyzingAll(false);
    }
  };

  const updateItem = (key, patch) => setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));

  const scheduleAll = async () => {
    if (!brandId) { toast.error("Pick a brand"); return; }
    if (!items.length) { toast.error("Upload images first"); return; }
    if (placements.length === 0) { toast.error("Pick at least one placement"); return; }
    const withoutCaption = items.filter((it) => !it.caption.trim()).length;
    if (withoutCaption > 0 && !confirm(`${withoutCaption} images have no caption — publish them empty?`)) return;
    setPublishing(true);
    try {
      const req = {
        brand_id: brandId,
        items: items.map((it) => ({
          image_url: it.image_url,
          caption: it.caption,
          hashtags: it.hashtags || null,
          topic: it.topic || null,
        })),
        placements,
        schedule_mode: scheduleMode,
        interval_minutes: Number(interval),
        append_brand_contact: appendContact,
      };
      if (scheduleMode === "custom") req.start_at = new Date(startAt).toISOString();
      const { data } = await api.post("/quick-post/bulk-schedule", req);
      toast.success(`Scheduled ${data.concepts} posts (${data.scheduled} placements) · every ${data.interval_minutes} min`);
      setItems([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Schedule failed");
    } finally {
      setPublishing(false);
    }
  };

  const togglePlacement = (k) => setPlacements((prev) => prev.includes(k) ? prev.filter((p) => p !== k) : [...prev, k]);

  return (
    <PageContainer>
      <PageHeader title="Bulk Post" subtitle="Upload multiple images. AI reads each one and writes a caption + hashtags. Review, then schedule all with one click." />

      {/* Top bar — brand + upload */}
      <div className="op-card p-4 mb-5 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-semibold text-gray-700 mb-1">Brand</label>
          <select className="op-input w-full text-sm" value={brandId} onChange={(e) => setBrandId(e.target.value)} data-testid="bp-brand">
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          onChange={(e) => { onFilesChosen(e.target.files); e.target.value = ""; }}
          className="hidden"
          data-testid="bp-file-input"
        />
        <button onClick={() => fileInputRef.current?.click()} className="op-btn-primary text-sm" data-testid="bp-upload-btn">
          <Upload size={14} /> Upload images ({items.length}/30)
        </button>
        <button
          onClick={analyzeAll}
          disabled={analyzingAll || !items.some((it) => !it.analyzed)}
          className="op-btn-secondary text-sm"
          data-testid="bp-analyze-all"
        >
          {analyzingAll ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {analyzingAll ? "Analyzing…" : "Analyze all (AI captions)"}
        </button>
      </div>

      {/* Items grid */}
      {items.length === 0 ? (
        <div className="op-card p-12 text-center border-2 border-dashed border-gray-300">
          <Upload size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-600 text-sm">No images uploaded yet.</p>
          <p className="text-xs text-gray-400 mt-1">Click &quot;Upload images&quot; above (max 30 · PNG/JPEG/WebP · 8 MB each).</p>
        </div>
      ) : (
        <div className="space-y-3 mb-6" data-testid="bp-items">
          {items.map((it, idx) => (
            <div key={it.key} className="op-card p-4 grid grid-cols-1 md:grid-cols-[180px_1fr_auto] gap-4">
              <div className="relative">
                <img src={it.image_url} alt="" className="w-full aspect-square object-cover rounded-lg" />
                <span className="absolute top-1 left-1 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full">#{idx + 1}</span>
                {it.analyzing && (
                  <div className="absolute inset-0 rounded-lg bg-black/50 flex items-center justify-center">
                    <Loader2 className="text-white animate-spin" size={24} />
                  </div>
                )}
              </div>
              <div className="min-w-0 space-y-2">
                {it.topic && <p className="text-xs text-gray-500">Topic: <b>{it.topic}</b> {it.subject && <span className="text-gray-400">· {it.subject}</span>}</p>}
                {it.error && <p className="text-xs text-rose-600">⚠ {it.error}</p>}
                <textarea
                  className="op-input w-full text-sm min-h-[100px]"
                  placeholder="Caption — click 'Analyze all' to have AI write one from the image"
                  value={it.caption}
                  onChange={(e) => updateItem(it.key, { caption: e.target.value })}
                  data-testid={`bp-caption-${idx}`}
                />
                <input
                  className="op-input w-full text-sm"
                  placeholder="Hashtags (with #)"
                  value={it.hashtags}
                  onChange={(e) => updateItem(it.key, { hashtags: e.target.value })}
                  data-testid={`bp-hashtags-${idx}`}
                />
              </div>
              <div className="flex md:flex-col items-start justify-start gap-2">
                <button onClick={() => removeItem(it.key)} className="p-2 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50" title="Remove" data-testid={`bp-remove-${idx}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Schedule settings */}
      {items.length > 0 && (
        <div className="op-card p-5 space-y-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Calendar size={16} className="text-[#7C3AED]" /> Schedule settings</h3>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">Post to</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="bp-placements">
              {PLACEMENT_OPTIONS.map((p) => (
                <label key={p.key} className="flex items-center gap-2 px-3 py-2 rounded-lg border-2 cursor-pointer text-sm"
                       style={{borderColor: placements.includes(p.key) ? "#7C3AED" : "#E5E7EB", background: placements.includes(p.key) ? "#FAF5FF" : "#fff"}}>
                  <input type="checkbox" checked={placements.includes(p.key)} onChange={() => togglePlacement(p.key)} />
                  <span>{p.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Start</label>
              <div className="flex gap-1 mb-2">
                <button onClick={() => setScheduleMode("now")}
                        className={`flex-1 px-3 py-2 rounded-lg text-xs border-2 ${scheduleMode === "now" ? "border-[#7C3AED] bg-purple-50 text-[#7C3AED] font-medium" : "border-gray-200"}`}>
                  Now
                </button>
                <button onClick={() => setScheduleMode("custom")}
                        className={`flex-1 px-3 py-2 rounded-lg text-xs border-2 ${scheduleMode === "custom" ? "border-[#7C3AED] bg-purple-50 text-[#7C3AED] font-medium" : "border-gray-200"}`}>
                  Custom
                </button>
              </div>
              {scheduleMode === "custom" && (
                <input type="datetime-local" className="op-input text-sm w-full" value={startAt} onChange={(e) => setStartAt(e.target.value)} data-testid="bp-start-at" />
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Interval between posts</label>
              <select className="op-input w-full text-sm" value={interval} onChange={(e) => setIntervalMin(Number(e.target.value))} data-testid="bp-interval">
                <option value={5}>Every 5 minutes</option>
                <option value={15}>Every 15 minutes</option>
                <option value={30}>Every 30 minutes</option>
                <option value={60}>Every 1 hour</option>
                <option value={120}>Every 2 hours</option>
                <option value={240}>Every 4 hours</option>
                <option value={720}>Every 12 hours</option>
                <option value={1440}>Once a day</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                <input type="checkbox" checked={appendContact} onChange={(e) => setAppendContact(e.target.checked)} />
                Auto-append brand phone/email/website
              </label>
            </div>
          </div>

          <div className="pt-3 border-t border-gray-100 flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-gray-500">
              {items.length} post{items.length === 1 ? "" : "s"} × {placements.length} placement{placements.length === 1 ? "" : "s"} = <b>{items.length * placements.length}</b> total drafts
            </p>
            <button
              onClick={scheduleAll}
              disabled={publishing || items.length === 0 || placements.length === 0}
              className="op-btn-primary text-sm"
              data-testid="bp-schedule-all"
            >
              {publishing ? <><Loader2 size={14} className="animate-spin" /> Scheduling…</> : <><Send size={14} /> Schedule all {items.length} posts</>}
            </button>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default BulkPost;
